import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Scale, BookOpen, ShoppingBag, Sparkles, Briefcase, Building2, GraduationCap, Share2, Newspaper, X, Play, Bell, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getBlogPosts, getActiveNotices } from '../utils/api';

const Home = () => {
  const [latestPosts, setLatestPosts] = useState([]);
  const [notices, setNotices] = useState([]);
  const [showPromoVideo, setShowPromoVideo] = useState(false);
  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);

  // Promo video config
  const promoVideo = {
    url: 'https://customer-assets.emergentagent.com/job_lexdo-ecommerce/artifacts/ssklyulv_Final_LexDoRight.mp4',
    startDate: '2026-01-08',
    endDate: '2026-01-18'
  };

  useEffect(() => {
    getBlogPosts()
      .then((res) => setLatestPosts(res.data.slice(0, 3)))
      .catch(console.error);
    
    // Fetch active notices
    getActiveNotices()
      .then((res) => setNotices(res.data))
      .catch(console.error);

    // Check if promo video should auto-play
    const today = new Date().toISOString().split('T')[0];
    const videoShown = sessionStorage.getItem('promoVideoShown');
    if (!videoShown && today >= promoVideo.startDate && today <= promoVideo.endDate) {
      setShowPromoVideo(true);
      sessionStorage.setItem('promoVideoShown', 'true');
    }
  }, []);

  // Auto-rotate notices every 5 seconds
  useEffect(() => {
    if (notices.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentNoticeIndex((prev) => (prev + 1) % notices.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [notices.length]);

  const goToNextNotice = () => {
    setCurrentNoticeIndex((prev) => (prev + 1) % notices.length);
  };

  const goToPrevNotice = () => {
    setCurrentNoticeIndex((prev) => (prev - 1 + notices.length) % notices.length);
  };

  return (
    <div className="min-h-screen">
      {/* Top Banner Notices - Carousel */}
      {notices.length > 0 && (
        <div className="bg-gradient-to-r from-[#8B1818] to-[#701313] text-white">
          <div className="py-3 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto flex items-center">
              {/* Left arrow - only show if multiple notices */}
              {notices.length > 1 && (
                <button 
                  onClick={goToPrevNotice}
                  className="mr-3 p-1 hover:bg-white/10 rounded transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
              
              {/* Notice content - left aligned */}
              <div className="flex items-center flex-1">
                <p className="text-sm sm:text-base font-medium">
                  <span className="font-bold">{notices[currentNoticeIndex]?.title}:</span>{' '}
                  {notices[currentNoticeIndex]?.content}
                  {notices[currentNoticeIndex]?.link && (
                    <a 
                      href={notices[currentNoticeIndex].link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="ml-2 text-[#FDB913] hover:underline font-semibold"
                    >
                      {notices[currentNoticeIndex].link_text || 'Learn More →'}
                    </a>
                  )}
                </p>
              </div>

              {/* Right side - dots indicator and arrow */}
              {notices.length > 1 && (
                <div className="flex items-center gap-3 ml-4">
                  {/* Dots indicator */}
                  <div className="flex gap-1.5">
                    {notices.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentNoticeIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          idx === currentNoticeIndex ? 'bg-[#FDB913]' : 'bg-white/40 hover:bg-white/60'
                        }`}
                      />
                    ))}
                  </div>
                  {/* Right arrow */}
                  <button 
                    onClick={goToNextNotice}
                    className="p-1 hover:bg-white/10 rounded transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Promotional Video Modal */}
      {showPromoVideo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative bg-black rounded-2xl overflow-hidden max-w-4xl w-full shadow-2xl">
            <button 
              onClick={() => setShowPromoVideo(false)}
              className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/30 rounded-full p-2 transition-colors"
            >
              <X className="h-6 w-6 text-white" />
            </button>
            <div className="aspect-video">
              <video 
                src={promoVideo.url}
                autoPlay
                controls
                className="w-full h-full object-contain"
                onEnded={() => setShowPromoVideo(false)}
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="p-4 bg-gradient-to-r from-[#8B1818] to-[#701313] text-white text-center">
              <p className="font-semibold">Welcome to <span className="text-[#FDB913]">Lex</span>DoRight!</p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="py-20 lg:py-28 bg-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="text-slate-900">Legal Clarity.</span>
                <br />
                <span className="bg-[#8B1818] text-white px-3 py-1 inline-block mt-2">Done Right.</span>
              </h1>
              
              <p className="text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl">
                We combine legal expertise, practical consulting, and accessible resources to empower our clients to make informed, ethical and compliant decisions.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/who-we-are" data-testid="who-we-are-button">
                  <button className="bg-[#8B1818] text-white w-52 h-14 text-lg font-semibold hover:bg-[#701313] transition-all duration-300 shadow-lg hover:shadow-xl">
                    Who We Are
                  </button>
                </Link>
                <Link to="/booking" data-testid="book-consultation-button">
                  <button className="bg-transparent border-2 border-[#8B1818] text-[#8B1818] w-52 h-14 text-lg font-semibold hover:bg-[#8B1818] hover:text-white transition-all duration-300">
                    Book Consultation
                  </button>
                </Link>
              </div>
            </div>

            {/* Right - Lady Justice with Cyber Elements */}
            <div className="relative" data-testid="hero-image">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_56e2863f-106a-4252-be25-54a4d7e6e68e/artifacts/7dnh974v_LadyJustice.PNG"
                  alt="Lady Justice with modern technology"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Share Website Button - Below picture */}
              <button 
                onClick={async () => {
                  const shareText = `Check out LexDoRight - Your partner for legal clarity, intelligent tools, and expert consultation. Visit: ${window.location.origin}`;
                  try {
                    await navigator.clipboard.writeText(shareText);
                    const btn = document.getElementById('share-btn');
                    if (btn) {
                      btn.textContent = 'Link Copied!';
                      setTimeout(() => {
                        btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="inline mr-1.5"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>Share Website';
                      }, 2000);
                    }
                  } catch (err) {
                    const input = document.createElement('input');
                    input.value = shareText;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                  }
                }}
                id="share-btn"
                className="absolute -bottom-5 right-0 inline-flex items-center px-3 py-1.5 bg-[#FDB913] text-[#8B1818] text-sm font-semibold rounded-lg hover:bg-[#e5a811] transition-colors shadow-md z-10"
              >
                <Share2 className="h-4 w-4 mr-1.5" />
                Share Website
              </button>
              {/* Watch Promo Video Link */}
              <button 
                onClick={() => setShowPromoVideo(true)}
                className="absolute -bottom-12 right-0 inline-flex items-center px-3 py-1.5 text-[#8B1818] text-sm font-medium hover:underline transition-colors z-10"
              >
                <Play className="h-4 w-4 mr-1.5" />
                Watch Promo Video
              </button>
              {/* Floating Quote Badge */}
              <div className="absolute -bottom-10 -left-8 bg-gradient-to-br from-[#8B1818] to-[#701313] p-6 rounded-2xl shadow-2xl border-4 border-[#FDB913] max-w-md">
                <p className="text-base font-bold text-white leading-relaxed">
                  At <span className="text-[#FFD700]">Lex</span><span className="text-white">DoRight</span>, we make it effortless to know the law, use powerful tools, and do what is right with precision.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions For Section */}
      <section className="py-16 bg-slate-50" data-testid="solutions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              Solutions for:
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="h-20 w-20 bg-[#8B1818]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Scale className="h-10 w-10 text-[#8B1818]" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Law Professionals</h3>
            </div>
            <div className="text-center">
              <div className="h-20 w-20 bg-[#8B1818]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Briefcase className="h-10 w-10 text-[#8B1818]" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Entrepreneurs & Business Owners</h3>
            </div>
            <div className="text-center">
              <div className="h-20 w-20 bg-[#8B1818]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="h-10 w-10 text-[#8B1818]" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Government</h3>
            </div>
            <div className="text-center">
              <div className="h-20 w-20 bg-[#8B1818]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="h-10 w-10 text-[#8B1818]" />
              </div>
              <h3 className="text-lg font-bold text-slate-900">Researchers & Students</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features with Images */}
      <section className="py-20 bg-white" data-testid="core-features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              Why Choose <span className="text-[#8B1818]">Lex</span>DoRight
            </h2>
            <p className="text-lg text-slate-600">Purpose-built solutions for organizations, institutions, and professionals seeking clarity and confidence in navigating the law.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1 */}
            <div className="bg-slate-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="aspect-[16/9] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&q=80&auto=format&fit=crop"
                  alt="Legal research"
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
                  Comprehensive Research
                </h3>
                <p className="text-slate-600 text-sm">
                  Access carefully curated legal, policy and governance materials with deep coverage across subject areas.
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-slate-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="aspect-[16/9] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80&auto=format&fit=crop"
                  alt="Intelligent tools"
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
                  Intelligent Tools
                </h3>
                <p className="text-slate-600 text-sm">
                  Use AI‑assisted drafting, case analysis and practical references integrated into your workflows.
                </p>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-slate-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="aspect-[16/9] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80&auto=format&fit=crop"
                  alt="Secure platform"
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
                  Professional Products
                </h3>
                <p className="text-slate-600 text-sm">
                  Choose from curated souvenirs, desk essentials, and eBooks that keep key legal concepts front and center.
                </p>
              </div>
            </div>

            {/* Feature 4 - Specialized Legal Advice */}
            <div className="bg-slate-50 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="aspect-[16/9] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/5716032/pexels-photo-5716032.jpeg?w=800&auto=compress&cs=tinysrgb"
                  alt="Specialized Legal Advice"
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-3" style={{fontFamily: 'Playfair Display, serif'}}>
                  Specialized Legal Advice
                </h3>
                <p className="text-slate-600 text-sm">
                  Book one-on-one sessions with legal experts via Viber for personalized guidance on your specific concerns.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leading with Purpose - Profile Section */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-slate-100" data-testid="leading-with-purpose-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
              Leading with Purpose
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              At <span className="text-[#8B1818] font-semibold">Lex</span>DoRight, we're inspired by trailblazers who bridge governance and innovation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Bio Block 1 - Vision & Mission */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
              <div className="aspect-[16/9]">
                <img src="/card1-photo.jpg" alt="SRAGCircle OPC Executives" className="w-full h-full object-cover grayscale" />
              </div>
              <div className="p-8 text-center">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Automation & Integrity</h3>
                <p className="text-slate-600 leading-relaxed">
                  The executives of <span className="font-semibold">SRAGCircle OPC</span> advance a governance culture grounded in accountability and innovation. The corporation's <span className="font-semibold">Head of Finance and Legal & Compliance</span> drives the company's mission to deliver automated, integrity‑driven solutions through technology.
                </p>
              </div>
            </div>

            {/* Bio Block 2 - LexDoRight Development */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
              <div className="aspect-[16/9]">
                <img src="/card2-photo.jpg" alt="Legal Technology Leader" className="w-full h-full object-cover grayscale" />
              </div>
              <div className="p-8 text-center">
                <h3 className="text-xl font-bold text-slate-900 mb-4">Legal Technology Leadership</h3>
                <p className="text-slate-600 leading-relaxed">
                  As a corporate lawyer, she leads the development of the <span className="text-[#8B1818] font-semibold">Lex</span>DoRight suite, including <span className="text-[#8B1818] font-semibold">Lex</span>Procure and <span className="text-[#8B1818] font-semibold">Lex</span>Agenda, the flagship platforms designed to strengthen transparency, accountability and efficiency in public procurement and legal processes.
                </p>
              </div>
            </div>

            {/* Bio Block 3 - GPPB Recognition */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
              <div className="aspect-[16/9] relative">
                <img src="/api/files/card3_bg.png" alt="GPPB Panel Discussion" className="w-full h-full object-cover grayscale" />
                <div className="absolute bottom-3 left-3 bg-gray-100 p-1.5 rounded-lg shadow-lg shadow-black/30 border border-gray-300">
                  <img src="/api/files/qr_code_mdrg.png" alt="QR Code" className="w-12 h-12 sm:w-14 sm:h-14" />
                </div>
              </div>
              <div className="p-8 text-center">
                <h3 className="text-xl font-bold text-slate-900 mb-4">GPPB Recognized Trainer</h3>
                <p className="text-slate-600 leading-relaxed">
                  A <span className="font-semibold">Recognized Trainer of the Government Procurement Policy Board (GPPB)</span>, she advances the professionalization of the public procurement workforce, sharing decades of experience in law, policy, and institutional governance with public and private sector stakeholders, including development partners.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Applications - Equal Grid */}
      <section className="py-24 bg-white" data-testid="featured-apps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Featured Applications
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Powerful tools designed for legal professionals, policymakers, and board secretaries
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* LexProcure PH - Green Badge, White Card */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-[#FDB913]/50 transition-colors card-hover" data-testid="app-lexprocure">
              <div className="inline-block px-3 py-1 bg-green-500 text-white rounded-full text-xs font-semibold mb-4">LIVE</div>
              <div className="relative p-2 border-2 border-[#FDB913] rounded-lg w-fit mb-4">
                <div className="border-2 border-[#FDB913] rounded-lg p-1">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_56e2863f-106a-4252-be25-54a4d7e6e68e/artifacts/p88jm6yj_LexProcurePHLogo.png" 
                    alt="LexProcure PH Logo" 
                    className="h-14 w-auto"
                  />
                </div>
              </div>
              <h3 className="text-xl font-bold mb-2">
                <span className="text-[#FDB913]">Lex</span>
                <span className="text-slate-900">Procure PH</span>
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Comprehensive procurement knowledge and workflow companion built specifically for Philippine legal and government organizations.
              </p>
              <Link to="/applications" className="text-[#8B1818] font-medium text-sm hover:underline" data-testid="learn-more-lexprocure">
                Learn More →
              </Link>
            </div>

            {/* LexDelta - Red Badge */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-[#FDB913]/50 transition-colors card-hover" data-testid="app-lexdelta">
              <div className="inline-block px-3 py-1 bg-red-500 text-white rounded-full text-xs font-semibold mb-4">COMING SOON</div>
              <div className="h-20 w-20 bg-white rounded-full flex items-center justify-center mb-4 p-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_56e2863f-106a-4252-be25-54a4d7e6e68e/artifacts/nzy29j4e_LexDelta_logo.png" 
                  alt="LexDelta Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold mb-2">
                <span className="text-slate-900">Lex</span>
                <span className="text-blue-600">Delta</span>
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Your smart drafting helper for everything that needs amending, such as contracts, laws, policies, rules, and high‑stakes agreements. It highlights what changes, what stays, and what conflicts, so you can focus on substance instead of manual redlines.
              </p>
              <Link to="/applications" className="text-[#8B1818] font-medium text-sm hover:underline" data-testid="learn-more-lexdelta">
                Learn More →
              </Link>
            </div>

            {/* LexAgenda - Yellow Badge */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-[#FDB913]/50 transition-colors card-hover" data-testid="app-lexagenda">
              <div className="inline-block px-3 py-1 bg-yellow-500 text-white rounded-full text-xs font-semibold mb-4">BETA</div>
              <div className="h-20 w-20 bg-slate-100 rounded-full flex items-center justify-center mb-4 p-3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_lexecom/artifacts/48ysnv4o_LexAgenda-final_logo-transparent.png" 
                  alt="LexAgenda Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <h3 className="text-xl font-bold mb-2">
                <span style={{color: '#00A7AE'}}>Lex</span>
                <span style={{color: '#FF8B00'}}>Agenda</span>
              </h3>
              <p className="text-slate-600 text-sm mb-4">
                Board secretary survival kit with meeting prep checklists, agenda templates, minutes scaffolds, and governance calendars.
              </p>
              <Link to="/applications" className="text-[#8B1818] font-medium text-sm hover:underline" data-testid="learn-more-lexagenda">
                Learn More →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Products Overview */}
      <section className="py-24 hero-pattern" data-testid="products-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
              Explore Our Offerings
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              From digital resources to professional giveaways
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* eBooks */}
            <Link to="/products/ebooks" data-testid="ebooks-card">
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 card-hover">
                <div className="aspect-[16/9]">
                  <img
                    src="https://images.unsplash.com/photo-1655388336851-b36513813283?w=800&q=80&auto=format&fit=crop"
                    alt="Legal eBooks"
                    className="w-full h-full object-cover grayscale"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">eBooks</h3>
                  <p className="text-slate-600 mb-4">
                    Comprehensive guides, templates, and resources for legal, policy, and governance professionals.
                  </p>
                  <span className="text-[#8B1818] font-medium inline-flex items-center">
                    Browse eBooks <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>

            {/* Souvenirs */}
            <Link to="/products/souvenirs" data-testid="souvenirs-card">
              <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-slate-100 card-hover">
                <div className="aspect-[16/9]">
                  <img
                    src="https://images.unsplash.com/photo-1643094829594-0ae1519d75af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBub3RlYm9vayUyMHBlbiUyMHdvb2RlbiUyMGRlc2slMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc2NTExMjE1MHww&ixlib=rb-4.1.0&q=85"
                    alt="Legal souvenirs"
                    className="w-full h-full object-cover grayscale"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Souvenirs</h3>
                  <p className="text-slate-600 mb-4">
                    Premium stationery, corporate gifts, and elegant office essentials for professionals.
                  </p>
                  <span className="text-[#8B1818] font-medium inline-flex items-center">
                    Browse Collection <ArrowRight className="ml-2 h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      {latestPosts.length > 0 && (
        <section className="py-24 bg-white" data-testid="blog-preview-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Latest Insights</h2>
                <p className="text-slate-600">Stay updated with relevant discussions and professional opinion or analysis</p>
              </div>
              <Link to="/blog" data-testid="view-all-posts-link">
                <Button variant="outline" className="hidden sm:flex">
                  View All Posts
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {latestPosts.map((post) => {
                // Calculate reading time (average 200 words per minute)
                const wordCount = post.content ? post.content.split(/\s+/).length : 0;
                const readingTime = Math.max(1, Math.ceil(wordCount / 200));
                
                return (
                  <Link to={`/blog/${post.id}`} key={post.id} data-testid={`blog-post-${post.id}`}>
                    <div className="bg-slate-50 rounded-xl overflow-hidden border border-slate-200 card-hover">
                      {post.image_url && (
                        <div className="aspect-[16/9]">
                          <img src={post.image_url} alt={post.title} className="w-full h-full object-cover grayscale" />
                        </div>
                      )}
                      <div className="p-6">
                        <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2">{post.title}</h3>
                        <p className="text-sm text-slate-600 line-clamp-3 mb-4">{post.excerpt}</p>
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>{readingTime} min read</span>
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            <div className="text-center mt-8 sm:hidden">
              <Link to="/blog">
                <Button variant="outline">View All Posts</Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Newsroom Banner */}
      <section className="py-20 relative" data-testid="newsroom-section">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_lexshop/artifacts/m30wto4d_Newsroom.png)'
          }}
        />
        {/* Light Overlay */}
        <div className="absolute inset-0 bg-slate-900/20" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="inline-flex flex-col md:flex-row items-center gap-4 bg-white/30 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/40">
            <div className="flex items-center gap-4">
              <div className="bg-[#FDB913] p-3 rounded-xl shadow-lg">
                <Newspaper className="h-7 w-7 text-[#8B1818]" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white drop-shadow-md">Newsroom</h3>
                <p className="text-white text-sm drop-shadow-sm">
                  Stay updated with Philippine law & tech news and <span className="text-[#FDB913] font-semibold">Lex</span>DoRight events
                </p>
              </div>
            </div>
            <Link to="/newsroom">
              <Button className="bg-[#FDB913] text-[#8B1818] hover:bg-[#e5a811] font-semibold px-5 py-2 shadow-lg text-sm">
                Visit Newsroom
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-[#8B1818] to-[#701313] text-white" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join professionals and organizations across legal, policy, and governance sectors who trust <span className="text-[#FDB913] font-semibold">Lex</span>DoRight for clarity and confidence.
          </p>
          <Link to="/register" data-testid="cta-get-started-button">
            <Button className="bg-white text-[#8B1818] hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold">
              Register Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;